<script lang="ts" setup>
type Steps = {
  title: string
  subTitle: string
  text: string
  callToAction: Record<string, unknown>
  media: Record<string, unknown>
}

type Props = {
  title: string
  steps: Steps[]
}

const _props = defineProps<{ data: Props; theme: string | null }>()

const { isMobileOrTablet } = useDevice()
const { width: innerWidth } = useWindowSize()

const isMobile = computed(() => {
  if (import.meta.server) return isMobileOrTablet
  return innerWidth.value < 900
})

const activeIndex = ref(0)
const stepContent = ref<HTMLElement | null>(null)
const handleClick = () => {
  stepContent.value?.scrollIntoView({
    behavior: 'smooth',
  })
}
</script>

<template>
  <div class="relative">
    <div class="mx-bleed relative border-t border-(--theme-fg)/15" />

    <div ref="stepContent" class="px-bleed grid gap-18 py-8">
      <h2 class="text-sm font-bold uppercase">{{ data.title }}</h2>
    </div>
    <div
      class="carousel-wrapper after:left-bleed sticky top-0 z-[4] h-fit w-full bg-(--theme-bg) pt-12 after:absolute after:bottom-0 after:z-[1] after:h-[1px] after:w-[calc(100%-var(--grid-bleed)*2)] after:bg-(--theme-fg)/30"
    >
      <CarouselsStepContent
        :data="data"
        :index="activeIndex"
        @active-index="activeIndex = $event"
        @click="handleClick"
      />
    </div>
    <div v-if="!isMobile" class="px-bleed lg:mt-0">
      <TransitionGroup name="list" tag="ul" class="relative">
        <li v-for="(item, idx) in data.steps" v-show="activeIndex === idx" :key="item.title">
          <!-- <div class="wrapper relative grid gap-5 lg:hidden">
            <client-only>
              <ElementsStepContentOverlay :data="item" class="relative" />
            </client-only>
          </div> -->
          <div class="wrapper hidden justify-between pt-8 lg:flex lg:pt-36">
            <div class="content w-cols-5 relative flex flex-col justify-between gap-10">
              <div class="text-content flex flex-col gap-10">
                <h3 class="w-cols-5 font-display text-lg lg:text-xl">{{ item.subTitle }}</h3>
                <div class="w-cols-5 flex flex-col gap-10 text-base">
                  <ElementsPortableTextWrapper :content="item.text" />
                </div>
              </div>
              <CommonBasicLink
                v-for="cta in item?.callToAction"
                :key="cta._key"
                :link="cta"
                variant="primary"
                class="w-cols-5"
              />
            </div>
            <div
              class="media-wrapper lg:w-cols-5 aspect-square h-max w-full overflow-hidden rounded-sm lg:sticky lg:top-40 lg:aspect-[6/7]"
            >
              <CommonMedia
                :media="item.media"
                :aspect-desktop="1"
                :aspect-mobile="1"
                class="h-full w-full"
                :sizes="'xs:100vw sm:100vw md:50vw lg:50vw xl:50vw xxl:50vw'"
              />
            </div>
          </div>
        </li>
      </TransitionGroup>
    </div>
    <div v-else class="mt-6">
      <CarouselsStepContentCard
        :data="data"
        :index="activeIndex"
        @active-index="activeIndex = $event"
      />
      <!-- <div class="wrapper relative grid gap-5 lg:hidden">
            <client-only>
              <ElementsStepContentOverlay :data="item" class="relative" />
            </client-only>
          </div> -->
    </div>
  </div>
</template>

<style scoped lang="postcss">
.embla {
  overflow: hidden;
  transition: background-color 0.5s ease-in-out;
}
.embla__container {
  display: flex;
  gap: 20px;
}
.embla__slide {
  --cols: 4;
  --gap: 20px;
  flex: 0 0 calc(var(--cols-4) - var(--grid-bleed));
  min-width: 0;
  @media (min-width: 900px) {
    flex: 0 0 calc((100% / var(--cols)) - (var(--gap) * (var(--cols) - 1) / var(--cols)));
  }
}

.list-move, /* apply transition to moving elements */
.list-enter-active,
.list-leave-active {
  transition: opacity 0.5s ease-in-out;
}

.list-enter-from {
  opacity: 0;
  /* transform: translateX(-30px); */
}

.list-leave-to {
  opacity: 0;
  /* transform: translateX(30px); */
}

/* ensure leaving items are taken out of layout flow so that moving
   animations can be calculated correctly. */
.list-leave-active {
  position: absolute;
  top: 0;
  width: 100%;
}
</style>
